import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import InventoryIcon from "src/assets/svg-icons/side-bar/inventory.svg";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import * as yup from "yup";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import CalcItem from "./calculation-item";
import { useGetBranches } from "src/api/generic";
import { useGetLevelsList } from "src/api/inventory/levels";
import {
  useAddProductRecipeMutation,
  useGetMenuProductById,
  useGetProductRecipeById,
  useUpdateProductRecipeMutation,
} from "src/api/inventory/menu";

const includeInOptions = [
  {
    label: "delivery",
    value: "delivery",
  },
  {
    label: "pickup",
    value: "pickup",
  },
  {
    label: "driveThru",
    value: "drive_through",
  },
  {
    label: "dineIn",
    value: "dine_in",
  },
];

const schema = yup.object().shape({
  branch_id: yup.string().required("required"),
  // recipe_quantity: yup.number().required("required"),
  items: yup
    .array()
    .of(
      yup.object().shape({
        item_id: yup.number().required("required"),
        item_name: yup.string(),
        item_sku: yup.string(),
        item_unit: yup.string(),
        item_unit_cost: yup.number(),
        item_quantity: yup.number().typeError("Required").required("required"),
        include_in: yup
          .array()
          .of(yup.string().required())
          .required()
          .min(1, "Required"),
        // calculations
        total: yup.number(),
      })
    )
    .required("Required"),
  modifiers: yup.array().of(
    yup.object().shape({
      mod_id: yup.number().required("required"),
      mod_name: yup.string(),
      mod_sku: yup.string(),
      mod_unit_cost: yup.number(),
      mod_quantity: yup.number().typeError("Required").required("required"),
      // calculations
      mod_total: yup.number(),
    })
  ),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const ProductRecipe = () => {
  const navigate = useNavigate();
  const { t: generalT } = useTranslation("general");
  const { t } = useTranslation("inventory");
  const { productId, recipeId } = useParams();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedModifier, setSelectedModifier] = useState<any>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [modInputValue, setModInputValue] = useState<string>("");
  const mode = !recipeId ? "add" : "edit";
  console.log({ mode });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const { fields, append, remove, update } = useFieldArray({
    name: "items",
    control,
  });

  const {
    fields: modifiersFields,
    append: appendModifier,
    remove: removeModifier,
    update: updateModifier,
  } = useFieldArray({
    name: "modifiers",
    control,
  });

  // APIS
  const {
    data: productData,
    status: getProByIdStatus,
    refetch,
    isFetching,
    isLoading,
  } = useGetMenuProductById({
    enabled: !!productId,
    id: productId as string,
  });

  const {
    data: recipeData,
    status: getRecipeStatus,
    refetch: refetchRecipe,
    isFetching: isFetchingRecipe,
    isLoading: isLoadingRecipe,
  } = useGetProductRecipeById({
    enabled: !!recipeId,
    id: recipeId as string,
  });

  const { data: itemsData, isFetching: isFetchingItems } = useGetLevelsList({
    branch_id: +watch("branch_id"),
  });

  const { data: branchesData } = useGetBranches();
  const { mutate, error, status, isPending } = useAddProductRecipeMutation();
  const {
    mutate: mutateUpdate,
    error: errorUpdate,
    status: statusUpdate,
    isPending: isPendingUpdate,
  } = useUpdateProductRecipeMutation();

  // permissions
  // const confirmPermission = specificBranchHasPermission(
  //   InventoryPermissions.confirmStocktaking,
  //   productData?.data.branch.id
  // );
  // const addPermission = specificBranchHasPermission(
  //   InventoryPermissions.addStocktaking,
  //   productData?.data.branch.id
  // );

  const enabledBranches =
    branchesData?.data.filter((branch) => branch.status) || [];

  const navigateToInventory = () => {
    navigate("/menu/products");
  };

  // Handler function when item is selected
  const handleItemSelect = (event: React.SyntheticEvent, newValue: any) => {
    setSelectedItem(newValue);
    if (newValue) {
      append({
        item_id: newValue.id,
        item_name: newValue.name,
        item_sku: newValue.sku,
        item_unit: newValue.unit?.name,
        item_quantity: 0,
        total: 0,
        item_unit_cost: newValue.unit_cost,
        include_in: ["delivery", "pickup", "drive_through", "dine_in"],
      });
      setInputValue("");
      setSelectedItem(null);
    }
  };

  // Handler function when modifier is selected
  const handleModifierSelect = (event: React.SyntheticEvent, newValue: any) => {
    setSelectedItem(newValue);
    if (newValue) {
      appendModifier({
        mod_id: newValue.id,
        mod_name: newValue.name,
        mod_sku: newValue.sku,
        mod_quantity: 0,
        mod_total: 0,
        mod_unit_cost: newValue.unit_cost ?? 0,
      });
      setModInputValue("");
      setSelectedModifier(null);
    }
  };

  // Handle manual changes to the input field of items
  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    if (selectedItem === null) {
      setInputValue(newInputValue);
    }
  };

  // Handle manual changes to the input field of modifiers
  const handleModifierInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    if (selectedModifier === null) {
      setModInputValue(newInputValue);
    }
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
  };

  const handleDeleteModifier = (index: number) => {
    removeModifier(index);
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    console.log({ data });
    const { branch_id, items, modifiers = [] } = data;
    const dataToSend = {
      branch_id: branch_id !== "dynamic" ? +branch_id : null,
      menu_id: Number(productId),
      items: items.map((item) => {
        return {
          item_id: item.item_id,
          quantity: item.item_quantity,
          methods: item.include_in,
        };
      }),
      modifiers: modifiers.map((mod) => {
        return {
          menu_id: mod.mod_id,
          quantity: mod.mod_quantity,
          unit_cost: mod.mod_unit_cost ?? 0,
        };
      }),
    };
    if (mode === "add") {
      mutate(dataToSend);
    } else {
      mutateUpdate({
        id: recipeId as string,
        ...dataToSend,
      });
    }
  };

  useEffect(() => {
    if (getRecipeStatus === "success") {
      reset({
        branch_id: !!recipeData.data?.branch?.id
          ? recipeData.data?.branch?.id
          : "dynamic",
        items: recipeData.data.menu_recipe_items.map((item: any) => {
          return {
            item_id: item.item?.id,
            item_name: item.item?.name,
            item_sku: item.item?.sku,
            item_unit: item.item?.unit?.name,
            item_quantity: item.quantity,
            item_unit_cost: item.item?.unit_cost,
            include_in: item.methods,
          };
        }),
        modifiers: recipeData.data.menu_recipe_modifiers.map(
          (modifier: any) => {
            return {
              mod_id: modifier.menu?.id,
              mod_name: modifier.menu?.name,
              mod_sku: modifier.menu?.sku,
              mod_quantity: modifier.quantity,
              mod_unit_cost: modifier.menu?.unit_cost ?? 0,
              // mod_total: modifier.total,
            };
          }
        ),
      });
    }
  }, [getRecipeStatus]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
      navigateToInventory();
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  const selectedItemsIds = watch("items")?.map((item) => item.item_id);

  const selectedModifiersIds = watch("modifiers")?.map(
    (modifier) => modifier.mod_id
  );

  let itemsCost = 0;

  watch("items")?.forEach((item) => {
    itemsCost += (item?.item_quantity ?? 0) * (item?.item_unit_cost ?? 0);
  });

  let modifiersCost = 0;

  watch("modifiers")?.forEach((mod) => {
    modifiersCost += (mod?.mod_quantity ?? 0) * (mod?.mod_unit_cost ?? 0);
  });

  const recipeCost = itemsCost + modifiersCost;

  console.log({ errors });

  return (
    <Stack gap={3} p={{ xs: 2, sm: 4 }}>
      <Stack
        direction={"row"}
        spacing={"12px"}
        alignItems={"center"}
        color={"#101828"}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"60px"}
          height={"60px"}
          bgcolor={"#D0D5DD"}
          borderRadius={"16px"}
        >
          <img src={InventoryIcon} width={"32px"} alt="" />
        </Box>
        <Stack>
          <Typography fontSize={"30px"} lineHeight={"38.4px"}>
            {productData?.data.name}
          </Typography>
          <Typography lineHeight={"38.4px"}>
            {t("totalCost")}:{" "}
            <strong>
              {watch("branch_id") !== "dynamic"
                ? `${recipeCost.toFixed(3)} SAR`
                : t("dynamic")}
            </strong>
          </Typography>
        </Stack>
      </Stack>
      {/* divider */}
      <Box bgcolor={"#F2F4F7"} width={"100%"} height={"1px"} />

      <Stack spacing={2} direction={"row"} mb={3} alignItems={"center"}>
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToInventory}
          sx={{ cursor: "pointer" }}
        >
          {t("inventory")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToInventory}
          sx={{ cursor: "pointer" }}
        >
          {t("products")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
          <Typography color={"#344054"} fontWeight={600} lineHeight={"19.2px"}>
            {/* {mode === "add" ? generalT("add") : productData?.data.reference} */}
            {productData?.data.name}
          </Typography>
        </Box>
      </Stack>

      {isLoading || isLoadingRecipe ? (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          minHeight={"250px"}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={2}>
              <Stack gap={2}>
                <Typography
                  color={"#344054"}
                  fontSize={"14px"}
                  fontWeight={700}
                  lineHeight={"16.8px"}
                >
                  {generalT("branch")}
                </Typography>
                <FormControl fullWidth size="small" error={!!errors.branch_id}>
                  <InputLabel id="demo-simple-select-label">
                    {generalT("branch")}
                  </InputLabel>
                  <Controller
                    name="branch_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label={generalT("branch")}
                        sx={{ maxWidth: "220px" }}
                      >
                        <MenuItem value={"dynamic"}>{t("dynamic")}</MenuItem>
                        {enabledBranches?.map(
                          ({ id, name, active_subscriptions }) => {
                            return (
                              <MenuItem
                                key={id}
                                value={`${id}`}
                                disabled={!active_subscriptions?.package.has_menu}
                              >
                                {name}
                              </MenuItem>
                            );
                          }
                        )}
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {generalT(`${errors.branch_id?.message ?? ""}`)}
                  </FormHelperText>
                </FormControl>
              </Stack>

              <Typography fontSize={"14px"} fontWeight={600}>
                {t("addItemsPRO")}
              </Typography>

              {fields.map((itemField, index) => {
                const totalCost =
                  watch("items")[index].item_quantity *
                  (watch("items")[index].item_unit_cost ?? 0);
                return (
                  <Box key={itemField.id} borderRadius={1.5}>
                    <Grid
                      container
                      spacing={{ xs: 1, md: 2 }}
                      alignItems={"center"}
                    >
                      <Grid item xs={11} sm={11}>
                        <Stack
                          direction={"row"}
                          gap={{ xs: 2, md: 6.5 }}
                          alignItems={"center"}
                          flexWrap={"wrap"}
                        >
                          <Box sx={{ minWidth: 180, width: 180 }}>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                            >
                              {t("item")}
                            </Typography>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                              fontWeight={700}
                            >
                              {itemField.item_name} ({itemField.item_unit})
                            </Typography>
                          </Box>

                          <Box sx={{ minWidth: 80, width: 80 }}>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                            >
                              {t("sku")}
                            </Typography>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                              fontWeight={700}
                              whiteSpace={"nowrap"}
                            >
                              {itemField.item_sku}
                            </Typography>
                          </Box>

                          <Controller
                            name={`items.${index}.item_quantity`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                type="number"
                                label={`${t("qnty")}`}
                                variant="outlined"
                                {...field}
                                error={!!errors.items?.[index]?.item_quantity}
                                helperText={
                                  errors.items?.[index]?.item_quantity?.message
                                }
                                fullWidth
                                size="small"
                                sx={{ minWidth: 170, width: 170 }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Typography fontSize={"14px"}>
                                        {itemField.item_unit}
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          />

                          <FormControl
                            size="small"
                            error={!!errors.items?.[index]?.include_in}
                          >
                            <InputLabel id="demo-multiple-checkbox-label">
                              {t("includeIn")}
                            </InputLabel>
                            <Controller
                              name={`items.${index}.include_in`}
                              control={control}
                              defaultValue={[]}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  size="small"
                                  sx={{ minWidth: 170, width: 170 }}
                                  labelId="demo-multiple-checkbox-label"
                                  id="demo-multiple-checkbox"
                                  multiple
                                  maxRows={2}
                                  input={
                                    <OutlinedInput label={t("includeIn")} />
                                  }
                                  renderValue={(selected) =>
                                    selected.map((value) => t(value)).join(", ")
                                  }
                                >
                                  {includeInOptions.map((item) => (
                                    <MenuItem
                                      key={item.value}
                                      value={item.value}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={
                                          field.value &&
                                          field.value.indexOf(item.value) > -1
                                        }
                                      />
                                      {t(item.label)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                            <FormHelperText id="my-helper-text">
                              {errors.items?.[index]?.include_in?.message}
                            </FormHelperText>
                          </FormControl>
                          {/* calculations */}
                          <CalcItem
                            label={t("unitCost")}
                            value={`${
                              watch("branch_id") !== "dynamic"
                                ? itemField.item_unit_cost?.toFixed(3) ?? 0
                                : t("dynamic")
                            }`}
                          />
                          <CalcItem
                            label={t("total")}
                            value={`${
                              watch("branch_id") !== "dynamic"
                                ? totalCost?.toFixed(3) ?? 0
                                : t("dynamic")
                            }`}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={1} md={1}>
                        <Box
                          display={"flex"}
                          justifyContent={"flex-end"}
                          alignItems={"center"}
                          sx={{ flexGrow: 1 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleDeleteItem(index);
                            }}
                          >
                            <img
                              src={DeleteIcon}
                              alt=""
                              width={"16px"}
                              style={{ cursor: "pointer" }}
                            />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
              <Autocomplete
                size="small"
                disablePortal
                id="combo-box-demo"
                options={
                  itemsData?.data.filter(
                    (item: any) => !selectedItemsIds?.includes(item.id)
                  ) ?? []
                }
                sx={{ width: 255 }}
                getOptionLabel={(option: any) => option.name}
                getOptionKey={(option) => `${option.id}`}
                value={selectedItem}
                onChange={handleItemSelect}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                renderInput={(params: any) => (
                  <TextField {...params} label={t("searchAndAddItem")} />
                )}
                loading={isFetchingItems}
                noOptionsText={
                  !watch("branch_id")
                    ? generalT("selectBranchFirst")
                    : generalT("noOption")
                }
              />

              {/* <Typography fontSize={"14px"} fontWeight={600}>
                {t("addModifiers")}
              </Typography> */}

              {/* modifiers */}
              {/* {modifiersFields.map((modifierField, index) => {
                const modifiers = watch("modifiers") ?? [];
                const totalCost =
                  modifiers?.[index].mod_quantity *
                  (modifiers?.[index].mod_unit_cost ?? 0);
                return (
                  <Box key={modifierField.id} borderRadius={1.5}>
                    <Grid
                      container
                      spacing={{ xs: 1, md: 2 }}
                      alignItems={"center"}
                    >
                      <Grid item xs={11} sm={11}>
                        <Stack
                          direction={"row"}
                          gap={{ xs: 2, md: 6.5 }}
                          alignItems={"center"}
                          flexWrap={"wrap"}
                        >
                          <Box sx={{ minWidth: 180, width: 180 }}>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                            >
                              {t("item")}
                            </Typography>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                              fontWeight={700}
                            >
                              {modifierField.mod_name}
                            </Typography>
                          </Box>

                          <Box sx={{ minWidth: 80, width: 80 }}>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                            >
                              {t("sku")}
                            </Typography>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                              fontWeight={700}
                              whiteSpace={"nowrap"}
                            >
                              {modifierField.mod_sku}
                            </Typography>
                          </Box>

                          <Controller
                            name={`modifiers.${index}.mod_quantity`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                type="number"
                                label={`${t("qnty")}`}
                                variant="outlined"
                                {...field}
                                error={
                                  !!errors.modifiers?.[index]?.mod_quantity
                                }
                                helperText={
                                  errors.modifiers?.[index]?.mod_quantity
                                    ?.message
                                }
                                fullWidth
                                size="small"
                                sx={{ minWidth: 170, width: 170 }}
                              />
                            )}
                          />

                          <CalcItem
                            label={t("unitCost")}
                            value={`${
                              modifierField.mod_unit_cost?.toFixed(3) ?? 0
                            }`}
                          />
                          <CalcItem
                            label={t("total")}
                            value={`${totalCost.toFixed(3)}`}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={1} md={1}>
                        <Box
                          display={"flex"}
                          justifyContent={"flex-end"}
                          alignItems={"center"}
                          sx={{ flexGrow: 1 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleDeleteModifier(index);
                            }}
                          >
                            <img
                              src={DeleteIcon}
                              alt=""
                              width={"16px"}
                              style={{ cursor: "pointer" }}
                            />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })} */}

              {/* <Autocomplete
                size="small"
                disablePortal
                id="combo-box-demo"
                options={
                  modifiersData?.data.filter(
                    (mod: any) => !selectedModifiersIds?.includes(mod.id)
                  ) ?? []
                }
                sx={{ width: 255 }}
                getOptionLabel={(option: any) => option.name}
                getOptionKey={(option) => `${option.id}`}
                value={selectedModifier}
                onChange={handleModifierSelect}
                inputValue={modInputValue}
                onInputChange={handleModifierInputChange}
                renderInput={(params: any) => (
                  <TextField {...params} label={t("searchAndAddItem")} />
                )}
                loading={isFetchingItems}
                // noOptionsText={
                //   !watch("branch_id")
                //     ? generalT("selectBranchFirst")
                //     : generalT("noOption")
                // }
              /> */}
            </Stack>

            <Stack
              spacing={2}
              direction={"row"}
              mt={3}
              justifyContent={"flex-end"}
            >
              <Button
                variant="outlined"
                color="tertiary"
                onClick={navigateToInventory}
                sx={{ minWidth: "180px" }}
              >
                {generalT("back")}
              </Button>

              <LoadingButton
                variant="contained"
                type="submit"
                loading={isPendingUpdate || isPending}
                sx={{ minWidth: "180px" }}
              >
                {generalT("save")}
              </LoadingButton>
            </Stack>
          </form>
        </>
      )}
    </Stack>
  );
};

export default ProductRecipe;
